import React from 'react'
import styled from 'styled-components'

// Generic map instance type with flyTo method
interface MapInstance {
  flyTo: (options: { center: [number, number]; zoom: number; duration: number }) => void
}

const isMapInstance = (
  value: React.RefObject<MapInstance | null> | MapInstance
): value is MapInstance => 'flyTo' in value

interface PDFMapRecenterButtonProps {
  map: React.RefObject<MapInstance | null> | MapInstance | null
  center: [number, number] // [lng, lat]
  zoom: number
  isPrint?: boolean
  accentColor?: string
}

const RecenterButton = styled.button<{ $accentColor?: string }>`
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 1000;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition:
    background 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: #f5f5f5;
    color: ${({ $accentColor }) => $accentColor || '#0294df'};
  }

  @media print {
    display: none;
  }
`

// Crosshair/target icon SVG
const CrosshairIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="22" y1="12" x2="18" y2="12" />
    <line x1="6" y1="12" x2="2" y2="12" />
    <line x1="12" y1="6" x2="12" y2="2" />
    <line x1="12" y1="22" x2="12" y2="18" />
  </svg>
)

/**
 * Map recenter button component for PDF export
 * Allows users to recenter the map on the first marker
 * Hidden in print mode
 */
export const PDFMapRecenterButton: React.FC<PDFMapRecenterButtonProps> = ({
  map,
  center,
  zoom,
  isPrint = false,
  accentColor,
}) => {
  if (isPrint) return null

  const handleRecenter = () => {
    if (!map) return
    const mapInstance = isMapInstance(map) ? map : map.current
    if (!mapInstance) return
    mapInstance.flyTo({
      center,
      zoom,
      duration: 500,
    })
  }

  return (
    <RecenterButton
      $accentColor={accentColor}
      onClick={handleRecenter}
      title="Recenter map"
      aria-label="Recenter map"
    >
      <CrosshairIcon />
    </RecenterButton>
  )
}

export default PDFMapRecenterButton
